import React, { useEffect, useState } from "react";
import { Select, Form } from "antd";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getAvailablePartnerStates, isPartnerStatesAutoPopulated } from "../../actions";

/**
 * Partner State Selector - on mount fetches all partners states
 * @component
 * @alias PartnerStateSelector
 * @property {number|array} initialValue - init value for select (state_id or array of state_ids)
 * @property {bool} readOnly - sets Form Item read-only
 * @property {bool} required - sets Form Item required - default false
 * @property {string} mode - select mode (single/multiple) - default "single"
 * @property {bool} autoPopulate - if true, will auto-populate from Redux store - default false
 * @property {object} form - Form instance for updating field values
 * @property {function} onAutoPopulate - callback when auto-population happens
 * @returns <Form.Item /> containing <Select />
 */
const PartnerStateSelector = ({ 
  initialValue, 
  required, 
  readOnly, 
  sendOnchange = null, 
  clearable = false, 
  hideLabel = false,
  mode = "single",
  autoPopulate = false,
  form = null,
  onAutoPopulate = null
}) => {
  const { t } = useTranslation();

  const [states, setStates] = useState([]);
  
  const availableStates = useSelector(getAvailablePartnerStates);
  const isAutoPopulated = useSelector(isPartnerStatesAutoPopulated);

  useEffect(() => {
    const states = t('partners.states', { returnObjects: true });
    setStates(states);
  }, [t]);

  useEffect(() => {
    if (autoPopulate && isAutoPopulated && availableStates.length > 0) {
      if (form) {
        form.setFieldsValue({ state: availableStates });
      }
      
      if (onAutoPopulate) {
        onAutoPopulate(availableStates);
      }
      
      if (sendOnchange) {
        sendOnchange(availableStates);
      }
    }
  }, [autoPopulate, isAutoPopulated, availableStates, form, onAutoPopulate, sendOnchange]);

  const handleChange = (value) => {
    if (sendOnchange) {
      sendOnchange(value);
    }
  };
  return (
    <Form.Item
      name="state"
      label={hideLabel ? undefined : t("partners.selectPartnerState")}
      rules={[
        {
          required: required,
          message: t("partners.validation.state"),
        },
      ]}
      initialValue={initialValue}
      style={hideLabel ? {width: 'auto'} : {minWidth: '370px'}}
    >
      <Select
        disabled={readOnly}
        allowClear={clearable}
        mode={mode === "multiple" ? "multiple" : undefined}
        onChange={handleChange}
      >
        {Object.keys(states).map((key) => {
          return (
            <Select.Option key={key} value={key}>
              {states[key]}
            </Select.Option>
          );
        })}
      </Select>
    </Form.Item>
  );
};

export default PartnerStateSelector;
